import { useState, useEffect, useRef } from 'react';
import { Modal, Button, message, Popconfirm } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { motion } from 'motion/react';
import ConfettiExplosion from 'react-confetti-explosion';
import dompurify from 'dompurify';
import { useApolloClient } from '@apollo/client';
import dayjs from 'dayjs';
import { downloadFromS3Signed } from 'src/_shared/services/utils.js';
import { isBackfilledAssignment } from 'src/rewardsAndRecognitionHelper/utils.js';
import {
	QUERY_REWARDS_STORE_TRANSACTIONS_BY_USER_ID,
	CREATE_REWARDS_STORE_TRANSACTION,
	CREATE_REWARDS_STORE_TRANSACTION_SNAPPY,
	GET_REWARDS_STORE_SETTINGS,
	QUERY_REWARDS_STORE_REWARDS,
} from 'src/rewardsStore/RewardsStoreGraphQL';

const CONFETTI_DURATION = 3000;

const confettiConfig = {
	force: 0.9,
	duration: CONFETTI_DURATION,
	particleCount: 300,
	width: '2000',
	height: 1600,
	colors: ['#f650a9', '#00c3ff', '#fffc00', '#00ff87', '#ff0055', '#7700ff'],
};

const MilestoneGiftModal = ({
	currentUser,
	badgeAssignments,
	visible,
	onClose,
}) => {
	const client = useApolloClient();
	const [isExploding, setIsExploding] = useState(false);
	const hasTriggered = useRef(false);
	const [claiming, setClaiming] = useState(false);
	const [declining, setDeclining] = useState(false);
	const [claimLink, setClaimLink] = useState(null);
	const [declineComplete, setDeclineComplete] = useState(false);
	const [unclaimedAssignment, setUnclaimedAssignment] = useState(null);
	const [milestoneReward, setMilestoneReward] = useState(null);
	const [loading, setLoading] = useState(true);

	const companyId = currentUser?.company?.id;
	const recognitionSettings = currentUser?.company?.recognitionSettings || {};
	const claimExpiryDays = recognitionSettings.milestoneClaimExpiryDays || 0;
	const modalTitle =
		recognitionSettings.milestoneClaimModalTitle || 'Claim Your Milestone Gift';
	const modalMessage = recognitionSettings.milestoneClaimModalMessage || '';

	const shouldShow =
		visible && !loading && unclaimedAssignment && milestoneReward;

	useEffect(() => {
		if (shouldShow && !hasTriggered.current) {
			hasTriggered.current = true;
			setIsExploding(true);
			const timer = setTimeout(() => {
				setIsExploding(false);
			}, CONFETTI_DURATION + 500);
			return () => clearTimeout(timer);
		}

		if (!visible) {
			hasTriggered.current = false;
			setIsExploding(false);
			setClaimLink(null);
			setDeclineComplete(false);
		}
	}, [visible, shouldShow]);

	useEffect(() => {
		if (visible && currentUser?.id && companyId && badgeAssignments) {
			checkForUnclaimedMilestones();
		}
	}, [visible, currentUser?.id, companyId, badgeAssignments, claimExpiryDays]);

	const checkForUnclaimedMilestones = async () => {
		try {
			setLoading(true);

			// Get assignments that have a milestone reward
			const milestoneAssignments = badgeAssignments.filter(
				(a) =>
					(a.badge?.milestoneRewardId || a.milestoneRewardId) &&
					!isBackfilledAssignment(a)
			);

			if (milestoneAssignments.length === 0) {
				setUnclaimedAssignment(null);
				setLoading(false);
				return;
			}

			// Fetch user's existing transactions
			let allTransactions = [];
			let nextToken = null;
			do {
				const { data } = await client.query({
					query: QUERY_REWARDS_STORE_TRANSACTIONS_BY_USER_ID,
					variables: {
						userId: currentUser.id,
						companyId,
						limit: 100,
						nextToken,
					},
					fetchPolicy: 'network-only',
				});
				if (data?.queryRewardsStoreTransactionsByUserId?.items) {
					allTransactions = [
						...allTransactions,
						...data.queryRewardsStoreTransactionsByUserId.items,
					];
					nextToken = data.queryRewardsStoreTransactionsByUserId.nextToken;
				} else {
					nextToken = null;
				}
			} while (nextToken);

			const transactionRewardIds = new Set(
				allTransactions.map((t) => t.rewardId)
			);

			// Find unclaimed assignments (no matching transaction for the milestone reward)
			const unclaimed = milestoneAssignments.filter((a) => {
				const rewardId = a.badge?.milestoneRewardId || a.milestoneRewardId;
				if (transactionRewardIds.has(rewardId)) return false;

				// Check expiry
				if (claimExpiryDays > 0) {
					const createdAt = a.rawAssignment?.createdAt || a.createdAt;
					if (createdAt) {
						const expiryDate = dayjs(createdAt).add(claimExpiryDays, 'day');
						if (dayjs().isAfter(expiryDate)) return false;
					}
				}

				return true;
			});

			if (unclaimed.length === 0) {
				setUnclaimedAssignment(null);
				setLoading(false);
				return;
			}

			// Use earliest unclaimed assignment
			const earliest = unclaimed.sort((a, b) => {
				const dateA = a.rawAssignment?.createdAt || a.createdAt || '';
				const dateB = b.rawAssignment?.createdAt || b.createdAt || '';
				return dateA.localeCompare(dateB);
			})[0];

			setUnclaimedAssignment(earliest);

			// Fetch the full reward with snappyProduct
			const rewardId =
				earliest.badge?.milestoneRewardId || earliest.milestoneRewardId;
			const { data: rewardsData } = await client.query({
				query: QUERY_REWARDS_STORE_REWARDS,
				variables: { companyId, rewardType: 'milestone', limit: 100 },
				fetchPolicy: 'cache-first',
			});

			const rewards =
				rewardsData?.queryRewardsStoreRewardsByCompanyIdRewardType?.items || [];
			const reward = rewards.find(
				(r) => r.id === rewardId && r.active !== false
			);
			if (!reward) {
				setUnclaimedAssignment(null);
			}
			setMilestoneReward(reward || null);
		} catch (error) {
			console.error('Error checking milestone claims:', error);
			setUnclaimedAssignment(null);
		} finally {
			setLoading(false);
		}
	};

	const handleClaimGift = async () => {
		if (!milestoneReward || !unclaimedAssignment) return;

		try {
			setClaiming(true);

			const useSnappyMutation = milestoneReward.snappyProduct?.id;

			let data;
			if (useSnappyMutation) {
				// Fetch store settings to get campaignId
				const { data: settingsData } = await client.query({
					query: GET_REWARDS_STORE_SETTINGS,
					variables: { companyId },
					fetchPolicy: 'cache-first',
				});
				const campaignId = settingsData?.getRewardsStoreSettings?.campaignId;

				const input = {
					companyId,
					userId: currentUser.id,
					campaignId: milestoneReward?.snappyCampaignId || campaignId,
					productId: milestoneReward.snappyProduct.id,
					rewardId: milestoneReward.id,
					amount: 0,
					budget: milestoneReward.snappyProduct.budget,
					firstName: currentUser.firstName,
					lastName: currentUser.lastName,
					email: currentUser.emailAddress,
					phone: '',
					isMilestoneCampaign: Boolean(milestoneReward?.snappyCampaignId),
				};

				const response = await client.mutate({
					mutation: CREATE_REWARDS_STORE_TRANSACTION_SNAPPY,
					variables: { input },
				});
				data = response.data;

				const transaction = data?.createRewardsStoreTransactionSnappy;
				if (transaction?.link) {
					setClaimLink(transaction.link);
				} else {
					message.success('Gift claimed successfully!');
				}
			} else {
				const response = await client.mutate({
					mutation: CREATE_REWARDS_STORE_TRANSACTION,
					variables: {
						input: {
							companyId,
							userId: currentUser.id,
							rewardId: milestoneReward.id,
							amount: 0,
						},
					},
				});
				data = response.data;

				const transaction = data?.createRewardsStoreTransaction;
				if (transaction?.link) {
					setClaimLink(transaction.link);
				} else {
					message.success('Gift claimed successfully!');
				}
			}
		} catch (error) {
			console.error('Error claiming milestone gift:', error);
			message.error(
				'Something went wrong claiming your gift. Please try again.'
			);
		} finally {
			setClaiming(false);
		}
	};

	const handleDeclineGift = async () => {
		if (!milestoneReward) return;

		try {
			setDeclining(true);

			await client.mutate({
				mutation: CREATE_REWARDS_STORE_TRANSACTION,
				variables: {
					input: {
						companyId,
						userId: currentUser.id,
						rewardId: milestoneReward.id,
						amount: 0,
						isDecline: true,
					},
				},
			});

			setDeclineComplete(true);
		} catch (error) {
			console.error('Error declining milestone gift:', error);
			message.error('Something went wrong. Please try again.');
		} finally {
			setDeclining(false);
		}
	};

	const certificateKey = unclaimedAssignment?.certificateKey;

	const handleDownloadCertificate = async () => {
		try {
			const signedUrl = await downloadFromS3Signed(
				certificateKey,
				'erin-documents'
			);
			if (!signedUrl) {
				message.error('Failed to download certificate.');
				return;
			}

			const response = await fetch(signedUrl);
			if (!response.ok) {
				message.error('Failed to download certificate.');
				return;
			}

			const blob = await response.blob();
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = 'certificate.pdf';
			document.body.append(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error downloading certificate:', error);
			message.error('Failed to download certificate.');
		}
	};

	const rewardImage =
		milestoneReward?.image ||
		unclaimedAssignment?.badge?.milestoneReward?.image ||
		unclaimedAssignment?.milestoneReward?.image;

	return (
		<Modal
			open={shouldShow}
			footer={null}
			onCancel={onClose}
			destroyOnClose
			centered
			width={480}
		>
			{isExploding && (
				<div
					style={{
						position: 'absolute',
						overflow: 'visible',
						top: '25%',
						left: '50%',
					}}
				>
					<ConfettiExplosion {...confettiConfig} />
				</div>
			)}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8, delay: 0.2 }}
				style={{ textAlign: 'center', padding: '16px 0' }}
			>
				<motion.h2
					initial={{ scale: 0.8, opacity: 0 }}
					animate={{ scale: 1, opacity: 1 }}
					transition={{
						duration: 0.8,
						delay: 0.3,
						type: 'spring',
						stiffness: 100,
					}}
					style={{ marginBottom: 16, fontSize: 22 }}
				>
					{modalTitle}
				</motion.h2>

				{rewardImage && (
					<motion.div
						initial={{ scale: 0.6, opacity: 0 }}
						animate={{ scale: 1, opacity: 1 }}
						transition={{
							duration: 0.8,
							delay: 0.5,
							type: 'spring',
							stiffness: 100,
						}}
						style={{
							display: 'flex',
							justifyContent: 'center',
							marginBottom: 16,
						}}
					>
						<img
							src={rewardImage}
							alt="Milestone reward"
							style={{
								height: 160,
								width: 'auto',
								maxWidth: '100%',
								objectFit: 'contain',
							}}
						/>
					</motion.div>
				)}

				{modalMessage && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ duration: 0.5, delay: 0.7 }}
						style={{
							fontSize: 15,
							lineHeight: 1.6,
							color: '#555',
							marginBottom: 24,
							padding: '0 8px',
						}}
						dangerouslySetInnerHTML={{
							__html: dompurify.sanitize(modalMessage),
						}}
					/>
				)}

				{declineComplete ? (
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5 }}
						style={{ marginBottom: 16 }}
					>
						<p style={{ fontSize: 15, color: '#555', marginBottom: 16 }}>
							Thank you for letting us know.
						</p>
						<div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
							<Button size="large" onClick={onClose}>
								Close
							</Button>
							{certificateKey && (
								<Button
									size="large"
									icon={<DownloadOutlined />}
									onClick={handleDownloadCertificate}
								>
									Download Certificate
								</Button>
							)}
						</div>
					</motion.div>
				) : claimLink ? (
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5 }}
						style={{ marginBottom: 16 }}
					>
						<p style={{ fontSize: 15, color: '#555', marginBottom: 16 }}>
							Your gift is ready!
						</p>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								gap: 8,
							}}
						>
							<Button
								type="primary"
								size="large"
								onClick={() =>
									window.open(claimLink, '_blank', 'noopener,noreferrer')
								}
							>
								Finish Claiming Your Gift
							</Button>
							<Button type="text" onClick={onClose}>
								Close
							</Button>
						</div>
					</motion.div>
				) : (
					<motion.div
						initial={{ opacity: 0, y: 10 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.5, delay: 1.0 }}
						style={{
							display: 'flex',
							justifyContent: 'center',
							gap: 12,
						}}
					>
						<Popconfirm
							title="Are you sure? This action cannot be undone."
							onConfirm={handleDeclineGift}
							okText="Yes"
							cancelText="No"
							disabled={claiming || declining}
						>
							<Button size="large" loading={declining} disabled={claiming}>
								Decline
							</Button>
						</Popconfirm>
						<Button
							type="primary"
							size="large"
							onClick={handleClaimGift}
							loading={claiming}
							disabled={declining}
						>
							Claim Gift
						</Button>
					</motion.div>
				)}
			</motion.div>
		</Modal>
	);
};

export default MilestoneGiftModal;
